import { useState, useEffect, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { fmtDuration, minutesToHours, hoursToMinutes } from "@/lib/utils";
import { DayBucketSelect, bucketValue, applyBucket } from "@/components/DayBucketSelect";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, Circle, Plus, Trash2 } from "lucide-react";
import { format, addDays, parseISO } from "date-fns";
import { pt } from "date-fns/locale";

const tomorrowOf = (date) => format(addDays(parseISO(date), 1), "yyyy-MM-dd");

function toRow(line) {
  const actual = line.actualMinutes != null ? line.actualMinutes : line.plannedMinutes;
  return {
    id: line.id || crypto.randomUUID(),
    taskId: line.taskId || "",
    title: line.title || "",
    clientId: line.clientId || "",
    projectId: line.projectId || "",
    internal: Boolean(line.internal || (!line.clientId && !line.projectId)),
    plannedMinutes: line.plannedMinutes || 0,
    actualHours: minutesToHours(actual),
    done: Boolean(line.done),
    action: line.carriedToDate ? "date" : "keep",
    carriedToDate: line.carriedToDate || "",
  };
}

const blankRow = () => ({
  id: crypto.randomUUID(),
  taskId: "",
  title: "",
  clientId: "",
  projectId: "",
  internal: true,
  plannedMinutes: 0,
  actualHours: 0.5,
  done: true,
  action: "keep",
  carriedToDate: "",
});

export function DayCheckoutModal({ open, onOpenChange, dayLog, onDone }) {
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && dayLog) {
      setRows((dayLog.lines || []).map(toRow));
      setNotes(dayLog.notes || "");
    }
  }, [open, dayLog]);

  const totalActual = useMemo(
    () => rows.reduce((s, r) => s + hoursToMinutes(r.actualHours), 0),
    [rows],
  );

  if (!dayLog) return null;

  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => setRows((rs) => rs.filter((r) => r.id !== id));

  const submit = async () => {
    const clean = rows.filter((r) => r.title.trim());
    for (const r of clean) {
      if (!r.internal && !r.clientId && !r.projectId) {
        return toast.error(`Indica cliente/projeto (ou marca interno) em: ${r.title}`);
      }
    }
    const lines = clean.map((r) => {
      let carriedToDate = "";
      if (!r.done && r.action === "tomorrow") carriedToDate = tomorrowOf(dayLog.date);
      if (!r.done && r.action === "date") carriedToDate = r.carriedToDate || "";
      return {
        id: r.id,
        taskId: r.taskId || "",
        title: r.title.trim(),
        clientId: r.internal ? "" : r.clientId || "",
        projectId: r.internal ? "" : r.projectId || "",
        internal: r.internal,
        plannedMinutes: r.plannedMinutes || 0,
        actualMinutes: hoursToMinutes(r.actualHours),
        done: r.done,
        carriedToDate,
        deleteTask: Boolean(!r.done && r.action === "delete" && r.taskId),
      };
    });
    setSaving(true);
    try {
      await api.post(`/day-logs/${dayLog.id}/close`, { lines, notes });
      toast.success("Dia fechado. Tempo registado.");
      onDone && onDone();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-background/60 backdrop-blur-md"
        className="max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="day-checkout-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-poppins">
            Fechar o dia · {format(parseISO(dayLog.date), "d 'de' MMMM", { locale: pt })}
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground font-roboto">
          Ajusta as horas reais, marca o que ficou feito e reagenda o resto. Cada linha com tempo gera um registo.
        </p>

        <div className="space-y-2 mt-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-lg border border-border bg-card p-2.5 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setRow(r.id, { done: !r.done })}
                  className={r.done ? "text-emerald-500" : "text-muted-foreground hover:text-primary"}
                  title={r.done ? "Concluída" : "Marcar como concluída"}
                >
                  {r.done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                </button>
                <Input
                  value={r.title}
                  onChange={(e) => setRow(r.id, { title: e.target.value })}
                  placeholder="Tarefa"
                  className="h-8 flex-1 min-w-[150px] text-sm"
                  disabled={Boolean(r.taskId)}
                />
                <DayBucketSelect
                  value={bucketValue(r)}
                  onChange={(v) => setRow(r.id, applyBucket(v))}
                  className="h-8 w-[180px] text-xs"
                />
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-muted-foreground">est. {fmtDuration(r.plannedMinutes)}</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.25"
                    value={r.actualHours}
                    onChange={(e) => setRow(r.id, { actualHours: e.target.value })}
                    className="h-8 w-16 text-sm"
                  />
                  <span className="text-xs text-muted-foreground">h</span>
                </div>
                <button onClick={() => removeRow(r.id)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {!r.done && (
                <div className="flex flex-wrap items-center gap-2 pl-6">
                  <Select value={r.action} onValueChange={(v) => setRow(r.id, { action: v })}>
                    <SelectTrigger className="h-7 w-[190px] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="keep">Deixar em aberto</SelectItem>
                      <SelectItem value="tomorrow">Mover para amanhã</SelectItem>
                      <SelectItem value="date">Mover para data…</SelectItem>
                      {r.taskId && <SelectItem value="delete">Apagar tarefa</SelectItem>}
                    </SelectContent>
                  </Select>
                  {r.action === "date" && (
                    <Input
                      type="date"
                      value={r.carriedToDate}
                      onChange={(e) => setRow(r.id, { carriedToDate: e.target.value })}
                      className="h-7 w-40 text-xs"
                    />
                  )}
                  {r.action === "tomorrow" && !r.taskId && (
                    <span className="text-[11px] text-muted-foreground">Só tarefas guardadas podem ser movidas.</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={() => setRows((rs) => [...rs, blankRow()])}
          className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
        >
          <Plus className="h-3.5 w-3.5" /> Adicionar algo que fiz e não estava no plano
        </button>

        <div className="mt-3">
          <Label className="text-sm">Notas do dia (opcional)</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 text-sm" />
        </div>

        <DialogFooter className="items-center">
          <Badge variant="secondary" className="rounded-full text-[11px] mr-auto">Total real: {fmtDuration(totalActual)}</Badge>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-primary text-black hover:bg-primary/90" data-testid="day-close-btn">
            {saving ? "A fechar..." : "Fechar o dia"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
